import { useEffect, useState } from 'react';

import {
  HiddenReasonType,
  Job,
  JobService,
  SitterDashboardData,
  SitterProfileStatus,
  SitterProfileStatusService,
} from 'us-web-services';
import { ProgressList } from 'us-react-components/dist/ProgressList/ProgressList';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  ApplyToJobsWidget,
  ApprovalChecklist,
  ArticlesAndResources,
  BackgroundCheckBanner,
  HiddenReasonBanner,
  HighlightGuide,
  JobBoard,
  SitterStatsList,
  SuccessGuide,
} from '../../components/Dashboard';
import { useDependencies } from '../../dependencies';

interface Props {
  dashboardData: SitterDashboardData;
}

interface ProgressItem {
  text: string;
  completed: boolean;
}

const MainContainer = ({ dashboardData }: Props): JSX.Element => {
  const { stores } = useDependencies();
  const { sitter: { sitter } } = stores;

  const { t } = useSharedTranslation(namespaces.caregiver);
  const [progressList, setProgressList] = useState<ProgressItem[]>();
  const [profileStatus, setProfileStatus] = useState<SitterProfileStatus>({});
  const [jobs, setJobs] = useState<Job[]>([]);

  const getProfileStatus = async (): Promise<void> => {
    if (sitter?.id) {
      try {
        const response = (await SitterProfileStatusService.get(sitter.id)).data;
        setProfileStatus(response.data);
      } catch (e) {
        console.warn(e);
      }
    }
  };

  const getJobBoard = async (): Promise<void> => {
    const config = {
      params: dashboardData.jobFilter,
      paramsSerializer: {
        indexes: null
      }
    };

    try {
      const response = (await JobService.getByFilter(config)).data;
      setJobs(response.data);
    } catch (e) {
      console.warn(e);
    }
  };

  const shouldShowApplyToJobsWidget = (): boolean => !dashboardData.hasLifetimeBookings && profileStatus.hiddenReason?.id === HiddenReasonType.UNHIDDEN;

  useEffect(() => {
    getProfileStatus();
    getJobBoard();

    if (dashboardData.progressList) {
      const list: ProgressItem[] = [];
      dashboardData.progressList.forEach(item => {
        const progressItem = {
          text: t(`pages.sitter.dashboard.progressList.${item.code}`),
          completed: item.completed
        };
        list.push(progressItem);
      });

      setProgressList(list);
    }
  }, []);

  return (
    <div className='w-full md:2/3 lg:w-3/4 md:ml-16'>
      <div className='w-full mb-10'>
        {dashboardData.showBackgroundCheckWarning && (
          <div className='mb-10'>
            <BackgroundCheckBanner dashboardData={dashboardData} />
          </div>
        )}
        <HiddenReasonBanner hasBookings={dashboardData.hasLifetimeBookings} />
        {jobs && jobs.length > 0 && (
          <JobBoard jobs={jobs} />
        )}
        {shouldShowApplyToJobsWidget() && (
          <ApplyToJobsWidget />
        )}
        {progressList && progressList.length > 0 && (
          <>
            <h4 className='mt-0 font-bold text-base'>{t('pages.sitter.dashboard.progressList.title')}</h4>
            <ProgressList items={progressList} />
          </>
        )}
        {dashboardData.showApprovalChecklist && (
          <div className='mb-10' id='caregiver-approval-checklist'>
            <ApprovalChecklist profileStatus={profileStatus} />
          </div>
        )}

        {dashboardData.showSuccessGuide && (
          <div className='mb-10'>
            <SuccessGuide />
          </div>
        )}
        {dashboardData.showHighlightGuide && (
          <div className='mb-10'>
            <HighlightGuide />
          </div>
        )}
        <div className='mb-10'>
          <SitterStatsList dashboardData={dashboardData} />
        </div>
      </div>
      <ArticlesAndResources />
    </div>
  );
}

export { MainContainer };
